import React, { useState, useEffect } from "react";

// Cookie utility functions
const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = getCookie("cookie_consent");
    if (!consent) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    setPreferences(allAccepted);
    setCookie("cookie_consent", JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    setPreferences(necessaryOnly);
    setCookie("cookie_consent", JSON.stringify(necessaryOnly));
    setShowBanner(false);
    setShowSettings(false);
  };

  const savePreferences = () => {
    setCookie("cookie_consent", JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (type) => {
    if (type === "necessary") return; // Cannot disable necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      <style>{`
        .cookie-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 9998;
        }
        
        .cookie-banner {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: #fff;
          padding: 24px 32px;
          box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 16px;
          animation: slideUp 0.3s ease-out;
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .cookie-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 20px;
        }
        
        .cookie-text {
          flex: 1;
          min-width: 300px;
        }
        
        .cookie-text h3 {
          margin: 0 0 8px 0;
          color: #1a1a2e;
          font-size: 1.25rem;
          font-weight: 600;
        }
        
        .cookie-text p {
          margin: 0;
          color: #555;
          font-size: 0.95rem;
          line-height: 1.5;
        }
        
        .cookie-text a {
          color: #0066cc;
          text-decoration: underline;
        }
        
        .cookie-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .cookie-btn {
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }
        
        .cookie-btn-accept {
          background: #1a1a2e;
          color: #fff;
        }
        
        .cookie-btn-accept:hover {
          background: #2d2d4a;
        }
        
        .cookie-btn-necessary {
          background: #e9ecef;
          color: #333;
        }
        
        .cookie-btn-necessary:hover {
          background: #dee2e6;
        }
        
        .cookie-btn-settings {
          background: transparent;
          color: #0066cc;
          border: 1px solid #0066cc;
        }
        
        .cookie-btn-settings:hover {
          background: #f0f7ff;
        }
        
        /* Settings Modal */
        .cookie-settings-modal {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: #fff;
          border-radius: 16px;
          padding: 32px;
          max-width: 500px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          z-index: 10000;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          animation: fadeIn 0.3s ease-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        
        .cookie-settings-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        
        .cookie-settings-header h2 {
          margin: 0;
          color: #1a1a2e;
          font-size: 1.5rem;
        }
        
        .cookie-close-btn {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #666;
          padding: 4px;
        }
        
        .cookie-close-btn:hover {
          color: #333;
        }
        
        .cookie-option {
          padding: 16px 0;
          border-bottom: 1px solid #eee;
        }
        
        .cookie-option:last-of-type {
          border-bottom: none;
        }
        
        .cookie-option-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        
        .cookie-option-title {
          font-weight: 600;
          color: #1a1a2e;
          font-size: 1rem;
        }
        
        .cookie-option-desc {
          color: #666;
          font-size: 0.9rem;
          line-height: 1.5;
        }
        
        /* Toggle Switch */
        .toggle-switch {
          position: relative;
          width: 50px;
          height: 26px;
        }
        
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 26px;
        }
        
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        
        .toggle-switch input:checked + .toggle-slider {
          background-color: #1a1a2e;
        }
        
        .toggle-switch input:checked + .toggle-slider:before {
          transform: translateX(24px);
        }
        
        .toggle-switch input:disabled + .toggle-slider {
          background-color: #1a1a2e;
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .cookie-settings-footer {
          margin-top: 24px;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        
        @media (max-width: 768px) {
          .cookie-banner {
            padding: 20px;
          }
          
          .cookie-content {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .cookie-buttons {
            width: 100%;
            justify-content: center;
          }
          
          .cookie-btn {
            flex: 1;
            text-align: center;
            padding: 12px 16px;
          }
          
          .cookie-settings-modal {
            padding: 24px;
          }
        }
      `}</style>

      {showSettings && <div className="cookie-overlay" onClick={() => setShowSettings(false)} />}

      {showSettings ? (
        <div className="cookie-settings-modal">
          <div className="cookie-settings-header">
            <h2>🍪 ตั้งค่าคุกกี้</h2>
            <button className="cookie-close-btn" onClick={() => setShowSettings(false)}>
              ✕
            </button>
          </div>

          <div className="cookie-option">
            <div className="cookie-option-header">
              <span className="cookie-option-title">คุกกี้ที่จำเป็น</span>
              <label className="toggle-switch">
                <input type="checkbox" checked={preferences.necessary} disabled />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p className="cookie-option-desc">
              คุกกี้เหล่านี้มีความจำเป็นสำหรับการทำงานของเว็บไซต์ ไม่สามารถปิดการใช้งานได้
            </p>
          </div>

          <div className="cookie-option">
            <div className="cookie-option-header">
              <span className="cookie-option-title">คุกกี้วิเคราะห์</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={() => handlePreferenceChange("analytics")}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p className="cookie-option-desc">
              ช่วยให้เราเข้าใจว่าผู้เข้าชมโต้ตอบกับเว็บไซต์อย่างไร เพื่อปรับปรุงประสบการณ์การใช้งาน
            </p>
          </div>

          <div className="cookie-option">
            <div className="cookie-option-header">
              <span className="cookie-option-title">คุกกี้การตลาด</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={() => handlePreferenceChange("marketing")}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p className="cookie-option-desc">
              ใช้เพื่อแสดงโฆษณาที่เกี่ยวข้องกับคุณและความสนใจของคุณ
            </p>
          </div>

          <div className="cookie-option">
            <div className="cookie-option-header">
              <span className="cookie-option-title">คุกกี้ฟังก์ชัน</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={() => handlePreferenceChange("functional")}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p className="cookie-option-desc">
              ช่วยจดจำการตั้งค่าของคุณ เช่น ภาษา หรือภูมิภาค เพื่อให้บริการที่ดีขึ้น
            </p>
          </div>

          <div className="cookie-settings-footer">
            <button className="cookie-btn cookie-btn-necessary" onClick={acceptNecessary}>
              เฉพาะที่จำเป็น
            </button>
            <button className="cookie-btn cookie-btn-accept" onClick={savePreferences}>
              บันทึกการตั้งค่า
            </button>
          </div>
        </div>
      ) : (
        <div className="cookie-banner">
          <div className="cookie-content">
            <div className="cookie-text">
              <h3>🍪 เว็บไซต์นี้ใช้คุกกี้</h3>
              <p>
                เราใช้คุกกี้เพื่อปรับปรุงประสบการณ์การใช้งานของคุณ วิเคราะห์การเข้าชมเว็บไซต์
                และนำเสนอเนื้อหาที่เหมาะสม โดยการคลิก "ยอมรับทั้งหมด" คุณยินยอมให้เราใช้คุกกี้
                <a href="/privacy-policy" style={{ marginLeft: "4px" }}>
                  นโยบายความเป็นส่วนตัว
                </a>
              </p>
            </div>
            <div className="cookie-buttons">
              <button className="cookie-btn cookie-btn-settings" onClick={() => setShowSettings(true)}>
                ตั้งค่า
              </button>
              <button className="cookie-btn cookie-btn-necessary" onClick={acceptNecessary}>
                เฉพาะที่จำเป็น
              </button>
              <button className="cookie-btn cookie-btn-accept" onClick={acceptAll}>
                ยอมรับทั้งหมด
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CookieConsent;
